import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import Header from "../../components/ui/Header";
import {Breadcrumb} from "../../components/ui/Breadcrumb";
import ProfilePhotoUpload from "./components/ProfilePhotoUpload";
import AccountStats from "./components/AccountStats";
import PrivacyControls from "./components/PrivacyControls";
import ProfileActions from "./components/ProfileActions";
import ExpenseSharingHistory from "./components/ExpenseSharingHistory";
import FrequentCollaborators from "./components/FrequentCollaborators";
import { getAuthData } from "utils/auth";
import apiService from "services/api";

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { user: currentUser } = getAuthData();
  // Form handling for profile editing
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      displayName: currentUser?.displayName || "",
      name: currentUser?.name || "",
      email: currentUser?.email || "",
      phone: currentUser?.phone || "",
      bio: currentUser?.bio || "",
    },
  });

  const handleEditToggle = () => {
    if (isEditing) {
      reset(); // Reset form to original values
    }
    setIsEditing(!isEditing);
  };

  // Your frontend functions updated with real API calls

  const onSubmitProfile = async (data) => {
    setIsLoading(true);

    try {
      const result = await apiService.updateProfile(data);

      if (result.success) {
        console.log("Profile updated:", result.data.user);

        // Update local storage
        localStorage.setItem("user", JSON.stringify(result.data.user));

        setIsLoading(false);
        setIsEditing(false);
        alert("Profile updated successfully!");
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error("Profile update error:", error);
      alert("Failed to update profile: " + error.message);
      setIsLoading(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    try {
      const result = await apiService.uploadAvatar(file);

      if (result.success) {
        console.log("Avatar uploaded:", result.data.avatar);

        // Update local storage
        const currentUser = apiService.getStoredUser();
        const updatedUser = { ...currentUser, avatar: result.data.avatar };
        apiService.storeUser(updatedUser);

        alert("Profile photo updated successfully!");
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error("Avatar upload error:", error);
      alert("Failed to upload photo: " + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Profile - ExpenseLoop</title>
        <meta
          name="description"
          content="Manage your ExpenseLoop profile, view account statistics, and control privacy settings for expense sharing."
        />
      </Helmet>

      {/* <Header user={currentUser} isAuthenticated={true} /> */}

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />

          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between flex-col sm:flex-row gap-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <svg
                    className="w-8 h-8 text-primary"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">
                    Profile
                  </h1>
                  <p className="text-muted-foreground">
                    Manage your account information and privacy settings
                  </p>
                </div>
              </div>

              <ProfileActions
                isEditing={isEditing}
                onEditToggle={handleEditToggle}
                onVerifyAccount={() => console.log("Verify account")}
                onChangePassword={() => console.log("Change password")}
                isVerified={currentUser?.isVerified}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Profile Section */}
            <div className="lg:col-span-2 space-y-6">
              {/* Profile Information Card */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-start space-x-6 flex-col md:flex-row justify-center">
                  {/* Profile Photo */}
                  <div className="flex-shrink-0 flex sm:justify-start justify-center w-full md:w-auto">
                    <ProfilePhotoUpload
                      currentPhoto={currentUser?.avatar}
                      onPhotoUpload={handlePhotoUpload}
                      disabled={!isEditing}
                      userName={currentUser?.name}
                    />
                  </div>

                  {/* Profile Form */}
                  <div className="flex-1">
                    <form
                      onSubmit={handleSubmit(onSubmitProfile)}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label
                            htmlFor="displayName"
                            className="block text-sm font-medium text-foreground mb-1"
                          >
                            Display Name *
                          </label>
                          {isEditing ? (
                            <input
                              id="displayName"
                              type="text"
                              {...register("displayName", {
                                required: "Display name is required",
                              })}
                              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                            />
                          ) : (
                            <p className="text-foreground font-medium py-2">
                              {currentUser?.displayName}
                            </p>
                          )}
                          {errors?.displayName && (
                            <p className="mt-1 text-sm text-error">
                              {errors?.displayName?.message}
                            </p>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor="name"
                            className="block text-sm font-medium text-foreground mb-1"
                          >
                            Full Name *
                          </label>
                          {isEditing ? (
                            <input
                              id="name"
                              type="text"
                              {...register("name", {
                                required: "Full name is required",
                              })}
                              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                            />
                          ) : (
                            <p className="text-foreground py-2">
                              {currentUser?.name}
                            </p>
                          )}
                          {errors?.name && (
                            <p className="mt-1 text-sm text-error">
                              {errors?.name?.message}
                            </p>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor="email"
                            className="block text-sm font-medium text-foreground mb-1"
                          >
                            Email Address *
                          </label>
                          {isEditing ? (
                            <input
                              id="email"
                              type="email"
                              {...register("email", {
                                required: "Email is required",
                                pattern: {
                                  value:
                                    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                  message: "Invalid email address",
                                },
                              })}
                              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                            />
                          ) : (
                            <div className="flex items-center space-x-2 py-2">
                              <p className="text-foreground">
                                {currentUser?.email}
                              </p>
                              {currentUser?.isVerified && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  <svg
                                    className="w-3 h-3 mr-1"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  Verified
                                </span>
                              )}
                            </div>
                          )}
                          {errors?.email && (
                            <p className="mt-1 text-sm text-error">
                              {errors?.email?.message}
                            </p>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor="phone"
                            className="block text-sm font-medium text-foreground mb-1"
                          >
                            Phone Number
                          </label>
                          {isEditing ? (
                            <input
                              id="phone"
                              type="tel"
                              {...register("phone")}
                              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                            />
                          ) : (
                            <p className="text-foreground py-2">
                              {currentUser?.phone}
                            </p>
                          )}
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="bio"
                          className="block text-sm font-medium text-foreground mb-1"
                        >
                          Bio
                        </label>
                        {isEditing ? (
                          <textarea
                            id="bio"
                            rows={3}
                            {...register("bio")}
                            placeholder="Tell others about yourself..."
                            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                          />
                        ) : (
                          <p className="text-foreground py-2">
                            {currentUser?.bio}
                          </p>
                        )}
                      </div>

                      <div className="text-sm text-muted-foreground">
                        <p>
                          Member since{" "}
                          {new Date(currentUser?.joinDate)?.toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )}
                        </p>
                      </div>

                      {isEditing && (
                        <div className="flex items-center space-x-3 pt-4 border-t border-border">
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50"
                          >
                            {isLoading ? (
                              <>
                                <svg
                                  className="animate-spin -ml-1 mr-2 h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                                Saving...
                              </>
                            ) : (
                              <>
                                <svg
                                  className="w-4 h-4 mr-2"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                                Save Changes
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={handleEditToggle}
                            className="px-4 py-2 border border-border rounded-md text-sm font-medium text-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              </div>

              {/* Privacy Controls */}
              <PrivacyControls />

              {/* Expense Sharing History */}
              <ExpenseSharingHistory />
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Account Statistics */}
                <AccountStats />

                {/* Frequent Collaborators */}
                <FrequentCollaborators />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
